import { GetServerSidePropsContext, InferGetServerSidePropsType } from 'next'
import Link from 'next/link'
import { useRouter } from 'next/router'
import * as React from 'react'
import { useEffect, useState } from 'react'

import { useBranding } from '@/hooks/use-branding'
import useLocale from '@/hooks/use-locale'

import { CourseGeneral, courseGenerals } from '@/data/course-general'
import { instructors } from '@/data/instructors'
import { instructorsEng } from '@/data/instructors-en'

import InstructorCard from '@/components/cards/instructor-card'
import ViewTracker from '@/components/common/view-tracker'
import ConsultationForm from '@/components/forms/consultation-form'
import LecturersSectionV2 from '@/components/sections/lecturers-section-v2'
import MoneyBackGuarantee from '@/components/sections/money-back-guarantee'
import PoweredSection from '@/components/sections/powered-section'
import ProjectFeedbackSection from '@/components/sections/project-feedback-section'
import RoadMap from '@/components/sections/road-map'
import {
  CourseModuleInformationContent,
  GroupModuleInformation,
  LeadingFormat,
  ModuleInformationHeading,
  ModuleInformationSection,
} from '@/components/sections/v3/module-information'
import SectionHallOfFame from '@/components/sections/v3/section-hall-of-fame'
import SectionHeroModuleDetail from '@/components/sections/v3/section-hero-module-detail'
import SEO from '@/components/seo/SEO'
import { Button } from '@/components/ui/button'
import RootContainer from '@/containers/root-container'

import { useAuth } from '@/contexts/auth-context'

export async function getServerSideProps(context: GetServerSidePropsContext) {
  const slug = context.params?.slug
  const query = context.query
  return {
    props: { courseId: slug, query },
  }
}

function ModuleDetailPage({ courseId }: InferGetServerSidePropsType<typeof getServerSideProps>) {
  const { text, lang } = useLocale()
  const { siteName } = useBranding()
  const course = courseGenerals.find(item => item.id === courseId) as CourseGeneral
  const instructorsFilter =
    lang === 'vi'
      ? instructors.filter(instructor => course?.instructors.includes(instructor.id))
      : instructorsEng.filter(instructor => course?.instructors.includes(instructor.id))

  const { user } = useAuth()
  const [isCourseEnrolled, setIsCourseEnrolled] = useState(false)
  const router = useRouter()
  const slug = router.asPath.split('/').pop()
  const courseSlug = router.query.c as string

  useEffect(() => {
    if (user) {
      const courseSlug = user?.courseUsers?.map(courseUser => courseUser.course.slug)
      if (courseSlug) setIsCourseEnrolled(courseSlug.some(sl => sl.includes(slug as string)))
    }
  }, [user, slug])

  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  const urlDetailCurriculum = lang === 'vi' ? course?.urlCurriculum?.[courseSlug] : course?.detailCourseEngUrl

  return (
    <RootContainer>
      <SEO title={`${siteName} - Module ${course?.courseName}`} />
      <ViewTracker entityType="page" entityId={`/courses/module/${String(courseId)}`} />
      <SectionHeroModuleDetail
        heading={`Module <span class="text-primary">${course?.title}</span>`}
        detailCourseUrl={urlDetailCurriculum || '#'}
        course={course}
        isCourseEnrolled={isCourseEnrolled}
        description={lang === 'vi' ? course?.description : course?.descriptionEng}
      />
      <RoadMap
        roadmapHeading={lang === 'vi' ? course?.roadmapHeading : course?.roadmapHeadingEng}
        roadmapUrl={course?.roadMapImage || '/images/home/fsds-road-map.png'}
      >
        <div className="flex justify-center gap-4">
          {lang === 'en' && (
            <Button asChild size="lg">
              <Link
                href={
                  isCourseEnrolled
                    ? '/my-courses'
                    : course?.id === 'mlops-1' && lang === 'en'
                      ? `/checkout?course=${course?.id}`
                      : router.asPath
                }
                rel="noopener noreferrer"
              >
                {isCourseEnrolled
                  ? text.MAIN.INTRODUCTION.HEADING_6
                  : course?.id === 'mlops-1'
                    ? text.MAIN.INTRODUCTION.HEADING_5
                    : text.MAIN.INTRODUCTION.HEADING_7}
              </Link>
            </Button>
          )}
          <Button asChild size="lg" variant="outline">
            <Link href={urlDetailCurriculum || '#'} rel="noopener noreferrer" target="_blank">
              {text.MAIN.BODY.COURSE.DETAIL_COURSE}
            </Link>
          </Button>
        </div>
      </RoadMap>
      <PoweredSection className="mt-20" />
      {lang === 'en' && <MoneyBackGuarantee course={course} isCourseEnrolled={isCourseEnrolled} />}
      <ModuleInformationSection course={course} isCourseEnrolled={isCourseEnrolled}>
        <ModuleInformationHeading title={text.COURSE_MODULE.OVERVIEW} />
        <GroupModuleInformation className="lg:grid-cols-2 max-w-5xl mx-auto">
          <CourseModuleInformationContent
            courseInformation={{
              finalProject: course?.finalProject || 1,
              schedule: course?.calendar || '',
              duration: ``,
              durationProject: `${course?.durationMakeProject ? `${course?.durationMakeProject} ${text.MAIN.COURSE.MONTH}` : ''}`,
              accessDuration: lang === 'en' ? `Unlimited` : `Không giới hạn`,
              labNumber: course?.labNumber || 20,
            }}
          />
          <LeadingFormat type={course?.type} />
          {/* {course && <DiscountModule courseModule={course} course={query?.c as string} />} */}
        </GroupModuleInformation>
      </ModuleInformationSection>
      <LecturersSectionV2>
        <div
          className={`mt-8 mb-16 ${instructorsFilter.length === 1 || lang === 'en' ? 'flex justify-center' : 'grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-2 sm:gap-4 md:gap-8'}`}
        >
          {instructorsFilter.length === 1 || lang === 'en' ? (
            <div key={instructorsFilter[0].id} className="flex justify-center">
              <InstructorCard {...instructorsFilter[0]} />
            </div>
          ) : (
            instructorsFilter.map(instructor => (
              <div key={instructor.id} className="flex justify-center">
                <InstructorCard {...instructor} />
              </div>
            ))
          )}
        </div>
      </LecturersSectionV2>
      <ConsultationForm />
      <SectionHallOfFame />
      {lang === 'vi' && <ProjectFeedbackSection className="mb-10" />}
    </RootContainer>
  )
}

export default ModuleDetailPage
